import React, { useEffect, useRef, useState } from "react";
import { getWallet, addWalletTransaction } from "../api";

const inputStyle = {
  backgroundColor: "var(--bg-nested)",
  border: "1px solid var(--border-color)",
  color: "var(--text-primary)",
  borderRadius: "8px",
  padding: "8px 12px",
  fontSize: "14px",
  outline: "none",
  width: "100%",
};

export default function WalletModal({ open, onClose, onSuccess }) {
  const [balance, setBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(true);

  const [type, setType] = useState("deposit");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setType("deposit");
    setAmount("");
    setNote("");
    setError(null);
    setLoadingBalance(true);
    getWallet()
      .then((w) => setBalance(w.balance))
      .catch((err) => setError(err.message))
      .finally(() => setLoadingBalance(false));
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  if (!open) return null;

  const handleSubmit = async () => {
    setError(null);
    const parsed = parseFloat(amount);
    if (!amount || isNaN(parsed) || parsed <= 0) {
      setError("Enter a valid amount.");
      return;
    }
    if (type === "withdrawal" && balance != null && parsed > balance) {
      setError("Amount exceeds your current cash balance.");
      return;
    }
    setSubmitting(true);
    try {
      await addWalletTransaction(type, parsed, note || null);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
      onClick={onClose}
    >
      <div
        className="rounded-xl p-6 w-full max-w-sm mx-4 elevate-card"
        style={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border-color)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold" style={{ color: "var(--text-primary)" }}>Wallet</h3>
          <button onClick={onClose} style={{ color: "var(--text-muted)" }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "var(--text-primary)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-muted)"; }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>
          Current cash balance:{" "}
          <span style={{ color: "var(--text-primary)" }}>
            {loadingBalance ? "…" : `$${(balance ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </span>
        </div>

        <div className="mb-4">
          <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>Type</label>
          <div className="flex rounded-lg overflow-hidden" style={{ border: "1px solid var(--border-color)" }}>
            {["deposit", "withdrawal"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className="flex-1 py-2 text-sm font-medium capitalize transition-colors"
                style={{
                  backgroundColor: type === t ? (t === "deposit" ? "var(--bg-green-soft)" : "var(--bg-red-soft)") : "var(--bg-page)",
                  color: type === t ? (t === "deposit" ? "var(--gain-green)" : "var(--loss-red)") : "var(--text-muted)",
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>Amount ($)</label>
          <input
            ref={inputRef}
            type="number"
            min="0.01"
            step="0.01"
            style={inputStyle}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="10000"
            onFocus={(e) => { e.currentTarget.style.borderColor = "var(--accent-blue)"; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border-color)"; }}
          />
        </div>

        <div className="mb-5">
          <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>Note (optional)</label>
          <input
            style={inputStyle}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Initial funding"
            onFocus={(e) => { e.currentTarget.style.borderColor = "var(--accent-blue)"; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border-color)"; }}
          />
        </div>

        {error && (
          <div className="mb-4 text-sm" style={{ color: "var(--loss-red)" }}>{error}</div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-2.5 rounded-lg text-sm font-semibold transition-colors"
          style={{
            backgroundColor: submitting ? "var(--bg-blue-soft)" : (type === "deposit" ? "var(--gain-green)" : "var(--loss-red)"),
            color: "#fff",
          }}
        >
          {submitting ? "Processing..." : type === "deposit" ? "Deposit" : "Withdraw"}
        </button>
      </div>
    </div>
  );
}
